'use client';

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '@/components/ui/AppIcon';

interface FAQ {
  id: string;
  question: string;
  answer: string;
  category: string;
}

const faqs: FAQ[] = [
  {
    id: '1',
    question: 'How can you really build an MVP in just 28 days?',
    answer:
      'We use a proven framework with pre-built components, established design systems, and agile development practices. Our team works in parallel across design, development, and testing phases. We focus on core features that validate your idea, not unnecessary bells and whistles.',
    category: 'Timeline',
  },
  {
    id: '2',
    question: 'Will the quality suffer with such a fast timeline?',
    answer:
      'Absolutely not. We maintain high quality standards through code reviews, automated testing, and QA processes. Our client satisfaction and on-time delivery record speak to our commitment to quality. We deliver production-ready code, not prototypes.',
    category: 'Quality',
  },
  {
    id: '3',
    question: 'What happens to my intellectual property and code?',
    answer:
      'You own 100% of the code, designs, and intellectual property. We sign NDAs before starting and transfer all assets to you upon project completion. We never reuse client-specific code or designs for other projects.',
    category: 'Legal',
  },
  {
    id: '4',
    question: 'Can I make changes during the 28-day development?',
    answer:
      'Yes, but within reason. Each engagement includes a specific number of design revisions. Major scope changes may affect the timeline. We recommend finalizing requirements in the Discovery phase to stay on track.',
    category: 'Process',
  },
  {
    id: '5',
    question: 'What if I need features beyond the initial MVP?',
    answer:
      'We offer post-launch development services to add features, scale infrastructure, or pivot based on user feedback. Many clients start with a focused MVP, validate their idea, then continue building.',
    category: 'Scaling',
  },
  {
    id: '6',
    question: 'Do you provide ongoing support after launch?',
    answer:
      'Yes. All plans include post-launch support ranging from 30 to 90 days depending on your engagement. This covers bug fixes, minor adjustments, and technical guidance. We also offer monthly maintenance packages.',
    category: 'Support',
  },
  {
    id: '7',
    question: 'Can you integrate with existing systems or APIs?',
    answer:
      'Absolutely. We have experience integrating with payment gateways, CRMs, marketing tools, and custom APIs. Integration complexity is factored into scoping during our initial consultation.',
    category: 'Technical',
  },
];

const FAQSection = () => {
  const [expandedId, setExpandedId] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredFAQs = faqs.filter(
    (faq) =>
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <section id="faq" className="section-pad section-rule relative z-10 bg-background">
      <div className="mx-auto max-w-3xl">
        <motion.div
          className="mb-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
        >
          <p className="mb-3 font-sans text-xs font-semibold uppercase tracking-[0.2em] text-primary">
            Common questions
          </p>
          <h2 className="mb-3 font-display text-2xl font-bold tracking-tight text-foreground sm:mb-4 sm:text-4xl lg:text-5xl">
            Frequently asked questions
          </h2>
          <p className="text-sm leading-relaxed text-text-secondary sm:text-lg">
            Everything you need to know about our 28-day MVP development process.
          </p>
        </motion.div>

        <div className="relative mb-8">
          <Icon
            name="MagnifyingGlassIcon"
            size={18}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-text-secondary"
          />
          <input
            type="text"
            placeholder="Search questions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full border border-border bg-card py-3 pl-11 pr-4 font-sans text-sm text-foreground placeholder:text-text-secondary focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>

        <div className="divide-y divide-border border-y border-border">
          {filteredFAQs.map((faq) => {
            const isExpanded = expandedId === faq.id;

            return (
              <div key={faq.id}>
                <button
                  onClick={() => setExpandedId(isExpanded ? '' : faq.id)}
                  className="flex w-full items-start justify-between gap-4 py-5 text-left transition-colors hover:bg-muted/40"
                >
                  <div>
                    <span className="mb-1.5 block font-sans text-[11px] font-semibold uppercase tracking-[0.15em] text-text-secondary">
                      {faq.category}
                    </span>
                    <h3 className="font-sans text-sm font-semibold text-foreground sm:text-base">
                      {faq.question}
                    </h3>
                  </div>
                  <Icon
                    name="ChevronDownIcon"
                    size={18}
                    className={`mt-1 shrink-0 text-text-secondary transition-transform duration-250 ${
                      isExpanded ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                <AnimatePresence initial={false}>
                  {isExpanded && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.25 }}
                      className="overflow-hidden"
                    >
                      <p className="pb-5 pr-8 text-sm leading-relaxed text-text-secondary sm:text-base">
                        {faq.answer}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>

        {filteredFAQs.length === 0 && (
          <p className="py-12 text-center text-sm text-text-secondary">
            No questions found. Try a different search term or contact us directly.
          </p>
        )}
      </div>
    </section>
  );
};

export default FAQSection;
